/**
 * The brain card's tab strip and the body behind each tab.
 *
 * Split out of `BrainCard` when the card grew a second way of drawing the same
 * tabs - a pinned menu down the left on its own page, the wrapping pill row
 * everywhere else. Both layouts read ONE list and render ONE body, so a tenth
 * tab is added here once instead of in two places that drift.
 *
 * The order is the order of an operator's day, not the order the pipeline runs
 * in: the verdict's arguments, then the letter written from them, then the words
 * for the call, then the evidence, and only then the raw stages.
 */
import type { LucideIcon } from "lucide-react";
import { Bug, Eye, FileText, Files, History, Link2, Mail, MessageSquareQuote, Phone } from "lucide-react";
import type { BrainPeerComparison, SiteBrain } from "../../../../lib/siteBrain";
import type { SideTab } from "../../../ui";
import type { T } from "../../site-scanner/types";
import { BrainArguments } from "./BrainArguments";
import { BrainContentPanel } from "./BrainContent";
import { BrainEmail } from "./BrainEmail";
import { BrainFindings } from "./BrainFindings";
import { BrainHistory } from "./BrainHistory";
import { BrainLook } from "./BrainLook";
import { BrainLinksPanel, BrainPagesPanel } from "./BrainStages";
import { BrainVisionPanel } from "./BrainVision";
import { BrainScript } from "./script";
import type { UseBrainHistory } from "./useHistory";

export type BrainTab =
  | "arguments"
  | "email"
  | "script"
  | "findings"
  | "history"
  | "look"
  | "content"
  | "links"
  | "pages";

const ICONS: Record<BrainTab, LucideIcon> = {
  arguments: MessageSquareQuote,
  email: Mail,
  script: Phone,
  findings: Bug,
  history: History,
  look: Eye,
  content: FileText,
  links: Link2,
  pages: Files,
};

/**
 * The tab list for one brain, counts included.
 *
 * The icons are carried here rather than in the card because the pinned menu
 * needs them - a column of entries with no glyph is a wall of words - and the
 * pill row simply ignores them.
 */
export function brainTabs(brain: SiteBrain | null, t: T): SideTab<BrainTab>[] {
  const tab = (value: BrainTab, count?: number): SideTab<BrainTab> => ({
    value,
    label: t(`brain.tab.${value}`),
    count,
    icon: ICONS[value],
  });

  return [
    tab("arguments", brain?.synthesis?.arguments.length ?? 0),
    // Second, right after the arguments it is written from. The letter is the
    // next thing an operator does with a verdict, so it sits where their hand
    // already is rather than at the end of the row with the raw stages.
    tab("email", brain?.emailDraft ? 1 : 0),
    // Third, beside the letter: the two are the same verdict said in two ways,
    // and an operator who has just read the draft is one click from the words
    // for the call rather than at the far end of the row with the raw stages.
    tab("script", brain?.callScript ? 1 : 0),
    tab("findings", brain?.findings.length ?? 0),
    // Right after the defects, because it is those defects over time. NO COUNT:
    // the history is not fetched until the tab is opened, so any number here
    // would be a zero that reads as "never measured before".
    tab("history"),
    tab("look", brain?.shots?.shots.length ?? 0),
    tab("content", brain?.content?.observations.length ?? 0),
    tab("links", brain?.links?.broken.length ?? 0),
    tab("pages", brain?.crawl?.pages.length ?? 0),
  ];
}

/** What one tab shows. One switch, so both layouts render the same body. */
export function BrainPanel({
  tab,
  brain,
  canManage,
  me,
  comparisons,
  history,
  onChanged,
  t,
}: {
  tab: BrainTab;
  brain: SiteBrain;
  canManage: boolean;
  me: string;
  comparisons: BrainPeerComparison[];
  history: UseBrainHistory;
  onChanged: () => void;
  t: T;
}) {
  switch (tab) {
    case "arguments":
      return <BrainArguments synthesis={brain.synthesis} findings={brain.findings} t={t} />;
    case "email":
      return (
        <BrainEmail
          domain={brain.domain}
          canManage={canManage}
          draft={brain.emailDraft}
          report={brain.report}
          hasSynthesis={brain.synthesis !== null}
          onChanged={onChanged}
          t={t}
        />
      );
    case "script":
      return (
        <BrainScript
          domain={brain.domain}
          canManage={canManage}
          script={brain.callScript}
          touch={brain.callTouch}
          fit={brain.synthesis?.fit ?? null}
          hasSynthesis={brain.synthesis !== null}
          onChanged={onChanged}
          t={t}
        />
      );
    case "findings":
      return <BrainFindings findings={brain.findings} comparisons={comparisons} t={t} />;
    case "history":
      return <BrainHistory state={history} me={me} t={t} />;
    case "look":
      return (
        <div className="space-y-4">
          <BrainLook layout={brain.layout} shots={brain.shots} t={t} />
          <BrainVisionPanel vision={brain.vision} findings={brain.findings} t={t} />
        </div>
      );
    case "content":
      return <BrainContentPanel content={brain.content} t={t} />;
    case "links":
      return brain.links ? (
        <BrainLinksPanel links={brain.links} t={t} />
      ) : (
        <p className="text-sm text-muted-foreground">{t("brain.links.missing")}</p>
      );
    case "pages":
      return brain.crawl ? (
        <BrainPagesPanel crawl={brain.crawl} t={t} />
      ) : (
        <p className="text-sm text-muted-foreground">{t("brain.pages.missing")}</p>
      );
  }
}
